import React from 'react';
import { Employee, TimesheetDayStatus } from '../../types';
import { cycleStatus, countWorkedDays, tableInputCls, monthLabel } from './timesheetUtils';
import DayCells from './DayCells';

export interface TimesheetRow {
  documentId?: string;
  id?: string | number;
  days: Record<string, TimesheetDayStatus>;
  description: string;
}

interface TimesheetGridProps {
  employees: Employee[];
  month: string; // yyyy-MM
  rows: Record<string, TimesheetRow>; // keyed by employee documentId
  onChange: (employeeKey: string, row: TimesheetRow) => void;
}

/** One stacked block per employee: name on top, the month's day cells below —
 *  nothing is squeezed into narrow table columns. */
const TimesheetGrid: React.FC<TimesheetGridProps> = ({ employees, month, rows, onChange }) => {
  const toggleDay = (employeeKey: string, day: number) => {
    const row = rows[employeeKey] || { days: {}, description: '' };
    const next = cycleStatus(row.days[String(day)]);
    onChange(employeeKey, { ...row, days: { ...row.days, [String(day)]: next } });
  };

  const setDescription = (employeeKey: string, description: string) => {
    const row = rows[employeeKey] || { days: {}, description: '' };
    onChange(employeeKey, { ...row, description });
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Crew Timesheet</h3>
        <span className="text-sm text-gray-500">{monthLabel(month)}</span>
      </div>

      {employees.length === 0 ? (
        <p className="text-center text-gray-500 py-10">No employees are assigned to this vessel</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {employees.map((emp) => {
            const key = emp.documentId || String(emp.id);
            const row = rows[key] || { days: {}, description: '' };
            const worked = countWorkedDays(row.days);
            return (
              <div key={key} className="px-5 py-4 hover:bg-gray-50/60">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
                  <div className="min-w-[220px]">
                    <div className="font-semibold text-gray-900">{emp.name}</div>
                    <div className="text-xs text-gray-500">{emp.rank || emp.position || '-'}</div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${worked > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}>
                    {worked} {worked === 1 ? 'day' : 'days'}
                  </span>
                  <div className="flex-1 min-w-[200px] max-w-md ml-auto">
                    <input
                      type="text"
                      value={row.description}
                      onChange={(e) => setDescription(key, e.target.value)}
                      placeholder="Notes..."
                      className={tableInputCls}
                    />
                  </div>
                </div>
                <DayCells month={month} days={row.days} onToggle={(day) => toggleDay(key, day)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TimesheetGrid;
